/**
 * Home taxonomy — what a US residential property contains: 22 spaces, 10
 * systems and 92 components, each component under exactly one system.
 *
 * Moved from `platform/src/lib/inspection/taxonomy.ts` (file-classification
 * Phase 1) so the platform, the webapp and Birdie share one list. A file's
 * subject is stored as these keys, never as evaluation row ids (reprocessing wipes
 * those). Human copy: docs/platform/files/vocabulary.md.
 *
 * Imports nothing: common is the floor package.
 */

/** The 22 space keys, in the order of the former `SPACE_DISPLAY_NAMES`. */
export const HOME_SPACE_KEYS = [
  "kitchen",
  "bedroom",
  "bathroom",
  "living_room",
  "family_room",
  "dining_room",
  "office",
  "laundry",
  "utility_room",
  "mudroom",
  "hallway",
  "sunroom",
  "staircase_interior",
  "other_room",
  "attic",
  "basement",
  "garage",
  "deck_porch_patio",
  "staircase_exterior",
  "driveway_walkway",
  "yard",
  "pool_area",
] as const;
export type HomeSpaceKey = (typeof HOME_SPACE_KEYS)[number];

/** Canonical display name of each space type. */
export const SPACE_DISPLAY_NAMES: Readonly<Record<HomeSpaceKey, string>> = {
  // Interior
  kitchen: "Kitchen",
  bedroom: "Bedroom",
  bathroom: "Bathroom",
  living_room: "Living Room",
  family_room: "Family Room",
  dining_room: "Dining Room",
  office: "Office / Study",
  laundry: "Laundry",
  utility_room: "Utility Room",
  mudroom: "Mudroom / Entry",
  hallway: "Hallway",
  sunroom: "Sunroom",
  staircase_interior: "Interior Staircase",
  other_room: "Other Room",
  // Fixed-location
  attic: "Attic",
  basement: "Basement / Crawlspace",
  garage: "Garage / Carport",
  // Exterior
  deck_porch_patio: "Deck, Porch & Patio",
  staircase_exterior: "Exterior Staircase",
  driveway_walkway: "Driveway & Walkway",
  yard: "Yard / Garden",
  pool_area: "Pool / Spa Area",
};

/** Deterministic space → location mapping ("Interior", "Attic", "Basement", "Garage", "Exterior"). */
export const SPACE_LOCATION: Readonly<Record<HomeSpaceKey, string>> = {
  // Interior (we don't guess floors)
  kitchen: "Interior",
  bedroom: "Interior",
  bathroom: "Interior",
  living_room: "Interior",
  family_room: "Interior",
  dining_room: "Interior",
  office: "Interior",
  laundry: "Interior",
  utility_room: "Interior",
  mudroom: "Interior",
  hallway: "Interior",
  sunroom: "Interior",
  staircase_interior: "Interior",
  other_room: "Interior",
  // Fixed-location
  attic: "Attic",
  basement: "Basement",
  garage: "Garage",
  // Exterior
  deck_porch_patio: "Exterior",
  staircase_exterior: "Exterior",
  driveway_walkway: "Exterior",
  yard: "Exterior",
  pool_area: "Exterior",
};

/** The 10 system keys — the former `SYSTEM_CATEGORIES` (renamed; the old name is gone). */
export const HOME_SYSTEM_KEYS = [
  "hvac",
  "plumbing",
  "electrical",
  "roofing",
  "fireplace",
  "water_management",
  "structural",
  "safety",
  "insulation",
  "appliances",
] as const;
export type HomeSystemKey = (typeof HOME_SYSTEM_KEYS)[number];

/** Canonical display name of each system type. */
export const SYSTEM_DISPLAY_NAMES: Readonly<Record<HomeSystemKey, string>> = {
  hvac: "HVAC System",
  plumbing: "Plumbing System",
  electrical: "Electrical System",
  roofing: "Roofing System",
  fireplace: "Fireplace & Chimney",
  water_management: "Water Management",
  structural: "Structural System",
  safety: "Safety Features",
  insulation: "Insulation & Ventilation",
  appliances: "Major Appliances",
};

/** The 92 component keys, in the order of the former `COMPONENT_TO_SYSTEM`. */
export const HOME_COMPONENT_KEYS = [
  "furnace",
  "air_handler",
  "ac_condenser",
  "heat_pump",
  "thermostat",
  "ductwork",
  "exhaust_fan",
  "mini_split",
  "humidifier",
  "dehumidifier",
  "air_purifier",
  "attic_fan",
  "water_heater",
  "water_main",
  "sump_pump",
  "water_softener",
  "water_filter",
  "garbage_disposal",
  "hose_bib",
  "backflow_preventer",
  "sewage_ejector",
  "pressure_regulator",
  "expansion_tank",
  "pex_manifold",
  "recirculation_pump",
  "electrical_panel",
  "sub_panel",
  "gfci_outlet",
  "afci_breaker",
  "light_fixture",
  "ceiling_fan",
  "surge_protector",
  "generator",
  "transfer_switch",
  "solar_panel",
  "solar_inverter",
  "ev_charger",
  "doorbell",
  "low_voltage_wiring",
  "roof_vent",
  "flashing",
  "skylight",
  "roof_membrane",
  "soffit",
  "fascia",
  "fireplace",
  "chimney",
  "gutter",
  "downspout",
  "french_drain",
  "irrigation",
  "sprinkler",
  "rain_barrel",
  "sump_well",
  "exterior_walls",
  "windows_doors",
  "ceiling_walls",
  "flooring",
  "garage_door",
  "foundation",
  "retaining_wall",
  "fence",
  "gate",
  "deck_structure",
  "porch_structure",
  "railing",
  "smoke_detector",
  "co_detector",
  "fire_extinguisher",
  "handrail",
  "guardrail",
  "alarm_system",
  "security_camera",
  "smart_lock",
  "fire_sprinkler",
  "attic_insulation",
  "wall_insulation",
  "pipe_insulation",
  "crawlspace_insulation",
  "weatherstripping",
  "range",
  "oven",
  "cooktop",
  "refrigerator",
  "dishwasher",
  "washer",
  "dryer",
  "microwave",
  "trash_compactor",
  "wine_cooler",
  "ice_maker",
  "outdoor_grill",
] as const;
export type HomeComponentKey = (typeof HOME_COMPONENT_KEYS)[number];

/** Every component maps to exactly one parent system. Gutters stay `water_management`. */
export const COMPONENT_TO_SYSTEM: Readonly<
  Record<HomeComponentKey, HomeSystemKey>
> = {
  // --- HVAC ---
  furnace: "hvac",
  air_handler: "hvac",
  ac_condenser: "hvac",
  heat_pump: "hvac",
  thermostat: "hvac",
  ductwork: "hvac",
  exhaust_fan: "hvac",
  mini_split: "hvac",
  humidifier: "hvac",
  dehumidifier: "hvac",
  air_purifier: "hvac",
  attic_fan: "hvac",

  // --- Plumbing ---
  water_heater: "plumbing",
  water_main: "plumbing",
  sump_pump: "plumbing",
  water_softener: "plumbing",
  water_filter: "plumbing",
  garbage_disposal: "plumbing",
  hose_bib: "plumbing",
  backflow_preventer: "plumbing",
  sewage_ejector: "plumbing",
  pressure_regulator: "plumbing",
  expansion_tank: "plumbing",
  pex_manifold: "plumbing",
  recirculation_pump: "plumbing",

  // --- Electrical ---
  electrical_panel: "electrical",
  sub_panel: "electrical",
  gfci_outlet: "electrical",
  afci_breaker: "electrical",
  light_fixture: "electrical",
  ceiling_fan: "electrical",
  surge_protector: "electrical",
  generator: "electrical",
  transfer_switch: "electrical",
  solar_panel: "electrical",
  solar_inverter: "electrical",
  ev_charger: "electrical",
  doorbell: "electrical",
  low_voltage_wiring: "electrical",

  // --- Roofing ---
  roof_vent: "roofing",
  flashing: "roofing",
  skylight: "roofing",
  roof_membrane: "roofing",
  soffit: "roofing",
  fascia: "roofing",

  // --- Fireplace & Chimney ---
  fireplace: "fireplace",
  chimney: "fireplace",

  // --- Water Management ---
  gutter: "water_management",
  downspout: "water_management",
  french_drain: "water_management",
  irrigation: "water_management",
  sprinkler: "water_management",
  rain_barrel: "water_management",
  sump_well: "water_management",

  // --- Structural ---
  exterior_walls: "structural",
  windows_doors: "structural",
  ceiling_walls: "structural",
  flooring: "structural",
  garage_door: "structural",
  foundation: "structural",
  retaining_wall: "structural",
  fence: "structural",
  gate: "structural",
  deck_structure: "structural",
  porch_structure: "structural",
  railing: "structural",

  // --- Safety ---
  smoke_detector: "safety",
  co_detector: "safety",
  fire_extinguisher: "safety",
  handrail: "safety",
  guardrail: "safety",
  alarm_system: "safety",
  security_camera: "safety",
  smart_lock: "safety",
  fire_sprinkler: "safety",

  // --- Insulation ---
  attic_insulation: "insulation",
  wall_insulation: "insulation",
  pipe_insulation: "insulation",
  crawlspace_insulation: "insulation",
  weatherstripping: "insulation",

  // --- Appliances ---
  range: "appliances",
  oven: "appliances",
  cooktop: "appliances",
  refrigerator: "appliances",
  dishwasher: "appliances",
  washer: "appliances",
  dryer: "appliances",
  microwave: "appliances",
  trash_compactor: "appliances",
  wine_cooler: "appliances",
  ice_maker: "appliances",
  outdoor_grill: "appliances",
};

/** Canonical display name of each component type. */
export const COMPONENT_DISPLAY_NAMES: Readonly<
  Record<HomeComponentKey, string>
> = {
  // HVAC
  furnace: "Furnace / Air Handler",
  air_handler: "Air Handler",
  ac_condenser: "AC Condenser / Heat Pump",
  heat_pump: "Heat Pump",
  thermostat: "Thermostat",
  ductwork: "Ductwork",
  exhaust_fan: "Exhaust / Ventilation Fan",
  mini_split: "Mini-Split / Ductless",
  humidifier: "Humidifier",
  dehumidifier: "Dehumidifier",
  air_purifier: "Air Purifier",
  attic_fan: "Attic Fan",
  // Plumbing
  water_heater: "Water Heater",
  water_main: "Water Main / Shutoff",
  sump_pump: "Sump Pump",
  water_softener: "Water Softener",
  water_filter: "Water Filter / RO System",
  garbage_disposal: "Garbage Disposal",
  hose_bib: "Hose Bib",
  backflow_preventer: "Backflow Preventer",
  sewage_ejector: "Sewage Ejector Pump",
  pressure_regulator: "Water Pressure Regulator",
  expansion_tank: "Expansion Tank",
  pex_manifold: "PEX Manifold",
  recirculation_pump: "Recirculation Pump",
  // Electrical
  electrical_panel: "Electrical Panel / Breaker",
  sub_panel: "Sub-Panel",
  gfci_outlet: "GFCI Outlets",
  afci_breaker: "AFCI Breakers",
  light_fixture: "Light Fixtures",
  ceiling_fan: "Ceiling Fan",
  surge_protector: "Whole-House Surge Protector",
  generator: "Generator",
  transfer_switch: "Transfer Switch",
  solar_panel: "Solar Panels",
  solar_inverter: "Solar Inverter",
  ev_charger: "EV Charger",
  doorbell: "Doorbell",
  low_voltage_wiring: "Low Voltage Wiring",
  // Roofing
  roof_vent: "Roof Vents",
  flashing: "Flashing",
  skylight: "Skylight",
  roof_membrane: "Roof Membrane",
  soffit: "Soffit",
  fascia: "Fascia",
  // Fireplace & Chimney
  fireplace: "Fireplace",
  chimney: "Chimney",
  // Water Management
  gutter: "Gutters",
  downspout: "Downspouts",
  french_drain: "French Drain",
  irrigation: "Irrigation / Sprinkler",
  sprinkler: "Sprinkler System",
  rain_barrel: "Rain Barrel",
  sump_well: "Sump Well",
  // Structural
  exterior_walls: "Exterior Walls / Siding",
  windows_doors: "Windows & Doors",
  ceiling_walls: "Interior Walls & Ceilings",
  flooring: "Flooring",
  garage_door: "Garage Door / Opener",
  foundation: "Foundation",
  retaining_wall: "Retaining Wall",
  fence: "Fence",
  gate: "Gate",
  deck_structure: "Deck Structure",
  porch_structure: "Porch Structure",
  railing: "Railing",
  // Safety
  smoke_detector: "Smoke Detector",
  co_detector: "CO Detector",
  fire_extinguisher: "Fire Extinguisher",
  handrail: "Handrail",
  guardrail: "Guardrail",
  alarm_system: "Security / Alarm System",
  security_camera: "Security Camera",
  smart_lock: "Smart Lock",
  fire_sprinkler: "Fire Sprinkler System",
  // Insulation
  attic_insulation: "Attic Insulation",
  wall_insulation: "Wall Insulation",
  pipe_insulation: "Pipe Insulation",
  crawlspace_insulation: "Crawlspace Insulation",
  weatherstripping: "Weatherstripping",
  // Appliances
  range: "Range / Oven / Cooktop",
  oven: "Oven",
  cooktop: "Cooktop",
  refrigerator: "Refrigerator",
  dishwasher: "Dishwasher",
  washer: "Washer",
  dryer: "Dryer",
  microwave: "Microwave (built-in)",
  trash_compactor: "Trash Compactor",
  wine_cooler: "Wine Cooler / Beverage Center",
  ice_maker: "Ice Maker",
  outdoor_grill: "Built-in Outdoor Grill",
};

/**
 * The `Category.title` a home-level file is filed under for its system (an
 * order's file takes its order's categories instead). Resolved to a
 * `Category.id` by title at read time by a later phase.
 */
export const SYSTEM_TRADE_TITLES: Readonly<Record<HomeSystemKey, string>> = {
  hvac: "HVAC",
  plumbing: "Plumbing",
  electrical: "Electrical",
  roofing: "Roofing",
  fireplace: "Fireplace",
  water_management: "Gutters",
  structural: "Carpentry",
  safety: "Security",
  insulation: "General Maintenance",
  appliances: "Appliances",
};

const SPACE_KEYS: ReadonlySet<string> = new Set(HOME_SPACE_KEYS);
const SYSTEM_KEYS: ReadonlySet<string> = new Set(HOME_SYSTEM_KEYS);
const COMPONENT_KEYS: ReadonlySet<string> = new Set(HOME_COMPONENT_KEYS);

/** Narrow an arbitrary string (an inspection label, a database column) to a space key. */
export function isHomeSpaceKey(
  value: string | null | undefined,
): value is HomeSpaceKey {
  return typeof value === "string" && SPACE_KEYS.has(value);
}

/** Narrow an arbitrary string to a system key. */
export function isHomeSystemKey(
  value: string | null | undefined,
): value is HomeSystemKey {
  return typeof value === "string" && SYSTEM_KEYS.has(value);
}

/** Narrow an arbitrary string to a component key. */
export function isHomeComponentKey(
  value: string | null | undefined,
): value is HomeComponentKey {
  return typeof value === "string" && COMPONENT_KEYS.has(value);
}

const COMPONENT_KEY_BY_NAME = new Map<string, HomeComponentKey>(
  HOME_COMPONENT_KEYS.flatMap((key) => [
    [COMPONENT_DISPLAY_NAMES[key].toLowerCase(), key] as const,
    [key, key] as const,
  ]),
);

const SPACE_KEY_BY_NAME = new Map<string, HomeSpaceKey>(
  HOME_SPACE_KEYS.flatMap((key) => [
    [SPACE_DISPLAY_NAMES[key].toLowerCase(), key] as const,
    [key, key] as const,
  ]),
);

/**
 * The component a display name ("Water Heater") or a raw key ("water_heater")
 * names — the inverse of `COMPONENT_DISPLAY_NAMES`, case-insensitive. A name
 * with an instance suffix ("Water Heater 2") must be split first.
 */
export function componentKeyOfName(name: string): HomeComponentKey | null {
  return COMPONENT_KEY_BY_NAME.get(name.trim().toLowerCase()) ?? null;
}

/** The space a display name ("Office / Study") or a raw key ("office") names — the inverse of `SPACE_DISPLAY_NAMES`. */
export function spaceKeyOfName(name: string): HomeSpaceKey | null {
  return SPACE_KEY_BY_NAME.get(name.trim().toLowerCase()) ?? null;
}
